import { useMemo } from "react";

import {
  ComponentType,
  FormkitComponent,
  RelationshipHierarchy,
} from "@hightouch/formkit";
import get from "lodash/get";
import uniq from "lodash/uniq";
import { useFormContext } from "react-hook-form";
import { isPresent } from "ts-extras";

import { OverrideConfig } from "src/components/destinations/types";
import { hasFieldValues } from "src/components/destinations/utils";
import { useFormkitContext } from "src/formkit/components/formkit-context";

import { OVERRIDE_CONFIG_FORM_KEY } from "./constants";

const LOCKED_FIELD_TOOLTIP =
  "To make this field uneditable, you must make a selection";

/**
 * Get all the ancestor or children keys recursively given a relationship diagram.
 */
export function getParentOrChildrenKeysRecursively({
  field,
  relationshipHierarchy,
  visited = new Set(),
}: {
  field: [fieldName: string, "ancestors" | "children"];
  relationshipHierarchy: RelationshipHierarchy;
  visited?: Set<string>;
}): string[] {
  const [key, relationship] = field;
  const node = get(relationshipHierarchy, key);

  if (!node) {
    return [];
  }

  if (visited.has(key)) {
    return [];
  }
  visited.add(key);

  if (relationship === "children") {
    if (!node.children) {
      return [];
    }

    return Array.from(node.children).flatMap((key) => [
      key,
      ...getParentOrChildrenKeysRecursively({
        field: [key, "children"],
        relationshipHierarchy,
        visited,
      }),
    ]);
  }

  if (node.ancestors.size === 0) {
    return [];
  }

  const result: string[] = [];
  for (const parentKey of node.ancestors) {
    result.push(
      parentKey,
      ...getParentOrChildrenKeysRecursively({
        field: [parentKey, "ancestors"],
        relationshipHierarchy,
        visited,
      }),
    );
  }

  return uniq(result);
}

/**
 * Helper function to check if any of the nodes have values
 */
export function hasNodeValues({
  nodes,
  formValues,
}: {
  nodes: FormkitComponent[];
  formValues: Record<string, any>;
}) {
  const fieldKeys = nodes.map((node) => node.key);
  return hasFieldValues(fieldKeys, formValues);
}

export function isSwitchChecked({
  nodes,
  relationshipHierarchy,
  overrideConfig,
}: {
  nodes: FormkitComponent[];
  relationshipHierarchy: RelationshipHierarchy | null;
  overrideConfig: OverrideConfig | null;
}) {
  if (!relationshipHierarchy) {
    return true;
  }

  if (!overrideConfig || Object.keys(overrideConfig).length === 0) {
    return true; // Default to locked when no override config exists or is empty
  }

  // Check if any nodes are journey settings fields
  const hasJourneySettingsNodes = nodes.some(
    (node) =>
      node.key.includes("journey_settings.mode") ||
      node.key.includes("journey_settings.exit_config") ||
      node.component === ComponentType.JourneySettings,
  );

  if (hasJourneySettingsNodes) {
    // For journey settings, check the journey_settings override
    const journeyOverride = get(overrideConfig, [
      "journey_settings",
      "overridable",
    ]);
    // Default to false (unlocked) if no explicit value is set
    return journeyOverride === undefined ? false : !journeyOverride;
  }

  // Determine if all of the regular nodes are locked
  return nodes.every((node) => {
    const nodeOverride = get(overrideConfig, [node.key, "overridable"]);
    // Default to true (locked) if no explicit value is set
    return nodeOverride === undefined ? true : !nodeOverride;
  });
}

/**
 * A hook that determines the rules around locking a field when using a sync template.
 *
 * @param nodes The formkit components
 * @param numberOfSyncLevelOverrides The number of sync-level overrides
 * @param allowAnyFieldLock Optional parameter to bypass locking rules and allow any field to be locked
 */
export function useSyncTemplateLockingRules(
  nodes: FormkitComponent[],
  numberOfSyncLevelOverrides: number = 0,
  opts?: { allowAnyFieldLock?: boolean },
): {
  isLocked: boolean;
  message: string | { label: string; children: string[] } | null;
} {
  const { allowAnyFieldLock = false } = opts || {};
  const { showLockUI, relationshipHierarchy } = useFormkitContext();
  const { watch, getValues } = useFormContext();

  const overrides = watch(OVERRIDE_CONFIG_FORM_KEY);
  const formValues = getValues();
  // Get the ancestor relationships for all of the nodes in this section
  const ancestorFields = useMemo(
    () =>
      relationshipHierarchy
        ? nodes.flatMap((node) =>
            getParentOrChildrenKeysRecursively({
              field: [node.key, "ancestors"],
              relationshipHierarchy,
            }),
          )
        : [],
    [nodes, relationshipHierarchy],
  );

  if (!showLockUI || !relationshipHierarchy) {
    return { isLocked: true, message: null };
  }

  // If allowAnyFieldLock is true, bypass all locking rules and allow locking
  if (allowAnyFieldLock) {
    // We should only disable locking if there's downstream syncs.
    if (numberOfSyncLevelOverrides > 0) {
      return {
        isLocked: false,
        message: "To lock this field, first clear all sync-level overrides",
      };
    }

    const isLocked = isSwitchChecked({
      nodes,
      relationshipHierarchy,
      overrideConfig: overrides,
    });
    return { isLocked, message: null };
  }

  // This field may not be overridden if it has an ancestor that is unlocked
  // Check if any of the nodes have values - lock can only be disabled if there's a value
  const nodeHasValues = hasNodeValues({ nodes, formValues });
  if (!nodeHasValues) {
    return {
      isLocked: false,
      message: LOCKED_FIELD_TOOLTIP,
    };
  }

  // This field may not be overridden if it has an ancestor that is unlocked
  const hasUnlockedAncestor = ancestorFields.some((parentKey) =>
    get(overrides, [parentKey, "overridable"]),
  );

  // Check if all nodes are mapping components
  const areAllMappingComponents = nodes.every(
    (node) =>
      node.component === ComponentType.Mapping ||
      node.component === ComponentType.Mappings ||
      node.component === ComponentType.AssociationMappings,
  );

  // Allow mapping components to be locked even if parents are unlocked
  if (hasUnlockedAncestor && !areAllMappingComponents) {
    const lockedAncestorFields = ancestorFields
      // Get unlocked fields
      .filter((field) => get(overrides, [field, "overridable"]))
      // Get ancestor fields (if any)
      .map((parentKey) => get(relationshipHierarchy, [parentKey, "heading"]))
      // Filter out fields that don't have ancestors
      .filter(isPresent);

    return {
      isLocked: false,
      message: {
        label: "To lock this field, first lock all parent fields",
        children: lockedAncestorFields,
      },
    };
  }

  if (numberOfSyncLevelOverrides > 0) {
    return {
      isLocked: false,
      message: "To lock this field, first clear all sync-level overrides",
    };
  }

  const isLocked = isSwitchChecked({
    nodes,
    relationshipHierarchy,
    overrideConfig: overrides,
  });

  return { isLocked, message: null };
}
